import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Omnibox } from '@local/client-contracts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isClickInElement } from '@shared/utils';
import { TagComponent } from '../suggestion-tag/suggestion-tag.component';
import { windowSizeObserver } from '@shared/utils';

@UntilDestroy()
@Component({
  selector: 'tag-list',
  templateUrl: './tag-list.component.html',
  styleUrls: ['./tag-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    // the fade-in/fade-out animation.
    trigger('fadeIn', [
      // the "in" style determines the "resting" state of the element when it is visible.
      state('in', style({ opacity: 1, transform: 'translateX(0)' })),

      // fade in when created. this could also be written as transition('void => *')
      transition(':enter', [style({ opacity: 0 }), animate(200)]),
    ]),
  ],
})
export class TagListComponent implements AfterViewInit, OnDestroy {
  private _model: Omnibox.Tag[];
  @Input() set model(v: Omnibox.Tag[]) {
    this._model = v;
    if (v.length <= 1) this.minimizeTags = false;
    this.markedChanged.emit('none');
  }
  @Input() removable: boolean = true;
  get model() {
    return this._model;
  }
  @Input() listMaxWidth: number = 50;
  @Output() remove: EventEmitter<Omnibox.Tag> = new EventEmitter<Omnibox.Tag>();
  @Output() markedChanged = new EventEmitter<'all' | 'partial' | 'none'>();
  private resizeObserver;

  /**
   * @description - max width in percentage relative to parent , so list can minimize itself if needed.
   * @examples - 70, 0 , 100, 30 ...
   * @default - 50
   */

  @ViewChild('list') listRef: ElementRef;
  @ViewChild('tagWidth') tagWidth: ElementRef;
  @ViewChildren('tag') tagsComponents: QueryList<TagComponent>;
  @ViewChildren('tag', { read: ElementRef }) tagsRef: QueryList<ElementRef>;
  @ViewChildren('transparentTag', { read: ElementRef }) transparentTagRefs: QueryList<ElementRef>;
  private windowSize$ = windowSizeObserver();

  minimizeTags: boolean = false;

  get isAllMarked(): boolean {
    if (this.tagsComponents.length === 0) return false;
    return this.tagsComponents.toArray().every((t) => t.selected);
  }

  get isSomeMarked(): boolean {
    if (this.tagsComponents.length === 0) return false;
    return this.tagsComponents.toArray().some((t) => t.selected);
  }

  get marked(): Omnibox.Tag[] {
    return this.tagsComponents
      .toArray()
      .filter((a) => a.selected)
      .map((a) => a.model);
  }

  constructor(private hostRef: ElementRef, private cdr: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    this.resizeObserver.unobserve(this.tagWidth.nativeElement);
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new (<any>window).ResizeObserver(() => {
      this.considerMinimize();
    });
    this.resizeObserver.observe(this.tagWidth.nativeElement);

    this.windowSize$.pipe(untilDestroyed(this)).subscribe(() => {
      this.considerMinimize();
    });
  }

  @HostListener('window:mousedown', ['$event'])
  onWindowClick(event: MouseEvent) {
    const currMarked = this.marked.length;
    for (const [i, tagRef] of this.tagsRef.toArray().entries()) {
      this.tagsComponents.toArray()[i].selected = isClickInElement(tagRef.nativeElement, event);
    }
    const nextMarked = this.marked.length;
    if (currMarked !== nextMarked) this.markedChanged.emit(nextMarked === 0 ? 'none' : 'partial');
  }

  trackTags(index: number, item: Omnibox.Tag): string {
    return item.id;
  }

  onRemove(tag: Omnibox.Tag) {
    this.remove.emit(tag);
  }

  considerMinimize() {
    const listEl = this.listRef?.nativeElement as HTMLUListElement;

    if (!listEl) return;

    let host = listEl.parentElement;
    let container = host.parentElement;

    const totalTagsWidth = this.tagWidth.nativeElement.clientWidth;

    const maxWidth = container.clientWidth * (this.listMaxWidth / 100);
    this.hostRef.nativeElement.style.maxWidth = `${maxWidth}px`;

    if (totalTagsWidth > maxWidth) {
      listEl.classList.add('split-equally');
      const calculatedWidth = container.clientWidth / this.tagsRef.length;
      const tags = this.transparentTagRefs.toArray();
      for (const [i, tagRef] of this.tagsRef.toArray().entries()) {
        const el: HTMLElement = tagRef.nativeElement;
        el.style.maxWidth = `${Math.min(calculatedWidth, tags[i].nativeElement.clientWidth)}px`;
      }
    } else {
      listEl.classList.remove('split-equally');
      this.tagsRef.forEach((t: ElementRef) => {
        const el: HTMLElement = t.nativeElement;
        el.style.maxWidth = `initial`;
      });
    }

    this.cdr.markForCheck();
  }

  markAll() {
    if (!this.model.length) return;
    this.tagsComponents.forEach((t) => (t.selected = true));
    this.markedChanged.emit('all');
  }

  unmarkAll() {
    if (!this.model.length) return;
    this.tagsComponents.forEach((t) => (t.selected = false));
    this.markedChanged.emit('none');
  }

  markNext(direction: 'right' | 'left') {
    if (direction === 'right') {
      const lastMarked = this.tagsComponents.find((t) => t.selected);
      if (lastMarked) lastMarked.selected = false;
    } else if (!this.isAllMarked) {
      const lastMarked = this.tagsComponents
        .toArray()
        .reverse()
        .find((t) => !t.selected);
      if (lastMarked) lastMarked.selected = true;
    }

    this.markedChanged.emit(!this.isSomeMarked ? 'none' : this.isAllMarked ? 'all' : 'partial');
  }

  unmarkNext(direction: 'right' | 'left') {
    if (direction === 'right') {
      const firstMarked = this.tagsComponents.find((t) => t.selected);
      if (firstMarked) firstMarked.selected = false;
    } else if (!this.isAllMarked) {
      const firstMarked = this.tagsComponents
        .toArray()
        .reverse()
        .find((t) => !t.selected);
      if (firstMarked) firstMarked.selected = true;
    }

    this.markedChanged.emit(!this.isSomeMarked ? 'none' : this.isAllMarked ? 'all' : 'partial');
  }

  onTagFocus(index: number) {
    this.tagsComponents.toArray().forEach((t, i) => (i !== index ? (t.selected = false) : null));
  }

  calculateTagsWidth(): number {
    const tagsEl = this.tagsRef.toArray().map((t) => t.nativeElement);

    let width = 0;
    for (let el of tagsEl) {
      width += el.clientWidth;
    }
    return width;
  }
}
